/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  ClickAwayListener,
  FormControl,
  IconButton,
  Paper
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import isNull from 'lodash.isnull';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {Meridiem} from '@ion/dls/src/components/daterangepicker/types';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import Render from '@ion/dls/src/render/Render';
import {
  dlsAlt,
  dlsColor,
  dlsTypography,
  dlsZIndex
} from '@ion/dls/src/theme/theme';
import {PrimaryButton} from '../../components/buttons/PrimaryButton';
import {toggleThemeStyle} from '../../theme/helpers';
import useThemeContext from '../../theme/useThemeContext';

type BackgroundStyles = {
  disabled?: boolean,
  useDisabledStyles?: boolean
};

export const useStyles = makeStyles(theme => {
  const getBackgroundColor = (isRoot: boolean) => {
    return ({disabled, useDisabledStyles}: BackgroundStyles, isRoot: boolean) => {
      if (disabled && useDisabledStyles) {
        return dlsAlt('components', 'dropdown', 'disabledBackground');
      }
      return isRoot ? 'transparent' : 'inherit';
    };
  };
  return {
    paperStyle : {
      top        : 50,
      left       : 0,
      padding    : 16,
      display    : 'flex',
      gap        : 12,
      zIndex     : dlsZIndex('contentLoaderContainer'),
      alignItems : 'center'
    },
    formControl : {
      width        : '100%',
      borderBottom : ({hasError}: { hasError: boolean; }) => (
        hasError ? 'none' : `1px solid ${dlsColor('grey5')}`
      ),
      '& div[class*="MuiInput-root"]' : {
        backgroundColor : getBackgroundColor(true),
        width           : '100%',
        marginTop       : 0,
        borderBottom    : ({hasError}: { hasError: boolean; }) => (
          hasError ? `solid 2px ${dlsColor('caRed')}` : 'none'
        )
      }
    },
    fieldLabel : {
      color        : dlsColor('grey2'),
      marginBottom : '8px',
      ...dlsTypography('body3')
    },
    currentFieldLabelError : {
      color : `${dlsColor('red')} !important`
    },
    requiredAsterisk : {
      color  : toggleThemeStyle(dlsColor('aaBlue'), dlsColor('red')),
      margin : '0 2px'
    },
    flexContainer : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'flex-start'
    },
    currentFlexContainer : {
      alignItems : 'center',
      height     : '32px'
    }
  } as any;
});

interface TimeInputProps {
  value?: string;
  fieldLabel?: string | ReactNode;
  useDisabledStyles?: Boolean;
  hasError?: boolean;
  errorText?: string;
  required?: boolean;
  openOnMount?: boolean;
  onChange?: (value: string) => void;
}

const padTimeValue = (value: number | string) => String(value).padStart(2, '0');

const getTimeOptions = (length: number, start = 0) => (
  Array.from({length}, (_, i) => padTimeValue(i + start))
);

const getMeridiemOptions = () => (
  [{label: Meridiem.AM, value: Meridiem.AM}, {label: Meridiem.PM, value: Meridiem.PM}]
);

const hours           = getTimeOptions(12, 1);
const minutes         = getTimeOptions(60);
const meridiemOptions = getMeridiemOptions();

const convertTo24Hour = (
  hour: string,
  minute: string,
  meridian: string
) => {
  let h = Number(hour);

  if (meridian === Meridiem.AM) {
    if (h === 12) {
      h = 0;
    }
  } else {
    if (h !== 12) {
      h += 12;
    }
  }

  return `${padTimeValue(h)}:${minute}`;
};

const TIME_PATTERN = /^(\d{1,2}):(\d{1,2})$/;

// returns the zero-padded HH:mm form of a complete, valid 24 hour time, otherwise `null` ...
const normalizeTime = (text: string): string | null => {
  const match  = TIME_PATTERN.exec(text?.trim() ?? '');
  const hour   = match ? Number(match[1]) : NaN;
  const minute = match ? Number(match[2]) : NaN;
  return hour <= 23 && minute <= 59 ? `${padTimeValue(hour)}:${padTimeValue(minute)}` : null;
};

interface Time12Hour {
  selectedHour: string;
  selectedMinute: string;
  selectedMeridian: 'AM' | 'PM';
}

export const convertTo12Hour = (time24: string): Time12Hour => {
  const [hourString, selectedMinute] = time24.split(':');

  let selectedHour = Number(hourString);
  let selectedMeridian: 'AM' | 'PM' = 'AM';

  if (selectedHour >= 12) {
    selectedMeridian = 'PM';
  }

  if (selectedHour === 0) {
    selectedHour = 12;
  } else if (selectedHour > 12) {
    selectedHour -= 12;
  }

  return {
    selectedHour : String(selectedHour).padStart(2, '0'),
    selectedMinute,
    selectedMeridian
  };
};

const TimeInput = ({
  onChange,
  hasError,
  errorText,
  value = '',
  required = false,
  useDisabledStyles = false,
  openOnMount = false,
  fieldLabel = ''
}: TimeInputProps) => {
  const classes: GenericObject = useStyles({
    useDisabledStyles,
    fieldLabel,
    hasError
  });

  const [inputValue, setInputValue]    = useState(value);
  const [open, setOpen]                = useState(false);
  const childRef                       = useRef(null);

  const {
    selectedHour,
    selectedMinute,
    selectedMeridian
  } = isNull(value)
    ? {selectedHour: '12', selectedMinute: '00', selectedMeridian: 'AM'} : convertTo12Hour(value);

  const [hour, setHour]         = useState(!selectedHour ? '00' : selectedHour);
  const [minute, setMinute]     = useState(!selectedMinute ? '00' : selectedMinute);
  const [meridian, setMeridian] = useState(selectedMeridian ?? Meridiem.AM);

  const [
    isAutocompleteMenuOpen,
    setIsAutoCompleteMenuOpen
  ] = useState<boolean>(openOnMount);

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  // the last value this component sent to its parent, so its own echo is not mistaken for an external change ...
  const lastEmittedRef = useRef<string>(null);
  // the last complete, valid time, restored when the user leaves the field with an unusable entry ...
  const lastValidRef   = useRef<string>(normalizeTime(value));

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      const selectedTime = convertTo24Hour(
        selectedHour,
        selectedMinute,
        selectedMeridian
      );
      setHour(selectedHour);
      setMinute(selectedMinute);
      setMeridian(selectedMeridian);
      setInputValue(selectedTime);
      lastValidRef.current = normalizeTime(selectedTime) ?? lastValidRef.current;
    }
  }, [value]);

  const emitTime = useCallback(function(time24: string) {
    lastEmittedRef.current = time24;
    lastValidRef.current   = time24;
    onChange?.(time24);
  }, [onChange]);

  const handleApply = useCallback(function() {
    const time24 = convertTo24Hour(
      hour,
      minute,
      meridian
    );

    setInputValue(time24);
    emitTime(time24);
    setOpen(false);
  }, [hour, minute, meridian, emitTime]);

  // keeps the typed text as-is and only reports complete, valid times to the parent ...
  const onInputChange = function(e: React.ChangeEvent<HTMLInputElement>) {
    const typed      = e.target.value;
    const normalized = normalizeTime(typed);
    setInputValue(typed);
    if (normalized) {
      const {selectedHour, selectedMinute, selectedMeridian} = convertTo12Hour(normalized);
      setHour(selectedHour);
      setMinute(selectedMinute);
      setMeridian(selectedMeridian);
      emitTime(normalized);
    }
  };

  // pads a valid entry, or restores the last valid time when the entry is incomplete or invalid ...
  const onInputBlur = function() {
    const resolved = normalizeTime(inputValue) ?? lastValidRef.current;
    if (resolved) {
      const {selectedHour, selectedMinute, selectedMeridian} = convertTo12Hour(resolved);
      setInputValue(resolved);
      setHour(selectedHour);
      setMinute(selectedMinute);
      setMeridian(selectedMeridian);
      if (resolved !== lastEmittedRef.current) {
        emitTime(resolved);
      }
    }
  };

  // close the menu when the user intract outside of dropdown menu...
  const handleClickOutside = useCallback((e) => {
    if (!childRef?.current?.contains(e.target)) {
      setIsAutoCompleteMenuOpen(false);
    }
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClickOutside}>
      <FormControl>
        {fieldLabel && (
          <div className={isCurrentTheme
            ? `${classes.flexContainer} ${classes.currentFlexContainer}`
            : classes.flexContainer}>
            <div className={isCurrentTheme && hasError
              ? `${classes.fieldLabel} ${classes.currentFieldLabelError}`
              : classes.fieldLabel}>
              {fieldLabel}
              {required && <span className={classes.requiredAsterisk}>*</span>}
            </div>
          </div>
        )}
        <div style={{position: 'relative'}} ref={childRef}>
          <FormTextField fullWidth
            value={inputValue}
            placeholder="HH:mm"
            {...(isCurrentTheme && {hasError, errorText})}
            onChange={onInputChange}
            onBlur={onInputBlur}
            InputProps={{
              endAdornment : (
                <IconButton size="small"
                  onClick={function() { setOpen(!open); }}>
                  <AccessTimeIcon />
                </IconButton>
              )
            }} />
          <Render.If condition={open}>
            <Paper elevation={4}
              className={classes.paperStyle}>
              <Dropdown autoComplete
                disableCloseOnSelect
                options={hours}
                fieldLabel={''}
                placeholder={''}
                value={hour}
                labelAsValue={false}
                onChange={function(e) {
                  setHour(e.target.value as string);
                }} />

                      :

              <Dropdown autoComplete
                disableCloseOnSelect
                options={minutes}
                fieldLabel={''}
                placeholder={''}
                value={minute}
                labelAsValue={false}
                onChange={function(e) {
                  setMinute(e.target.value as string);
                }} />

              <Dropdown autoComplete
                disableCloseOnSelect
                options={meridiemOptions}
                fieldLabel={''}
                placeholder={''}
                value={meridian}
                labelAsValue={false}
                onChange={function(e) {
                  setMeridian(e.target.value as string);
                }} />

              <Render.If condition={isLegacyTheme}>
                <button onClick={handleApply}>
                  {translate('common:ok')}
                </button>
              </Render.If>
              <Render.If condition={isCurrentTheme}>
                <PrimaryButton onClick={handleApply}>
                  <span>{translate('common:ok')}</span>
                </PrimaryButton>
              </Render.If>
            </Paper>
          </Render.If>
        </div>
      </FormControl>
    </ClickAwayListener>
  );
};

export default memo(TimeInput);
